import type { CSSProperties, ReactNode } from 'react';
import { type Avatar, type Slot, slotLabel } from '../models/types';
import { avatarBodyFor } from './bodies';
import { type AvatarBox, type WornLayer, resolveWornLayer } from './itemArt';

/** Back to front: garments first, the companion last. */
export const STACK_ORDER: readonly Slot[] = ['legs', 'body', 'feet', 'neck', 'face', 'head', 'hand', 'companion'];

/** Smallest tap target, as a fraction of the avatar's side, so small parts stay easy to hit. */
const MIN_TARGET = 0.16;

interface Props {
  avatar: Avatar;
  size?: number;
  /** Every part of the avatar reports the slot that was tapped. */
  onSlotTap?: (slot: Slot) => void;
  /** How an item is worn; defaults to its exported layer. Tests inject their own. */
  resolveWorn?: (itemId: string) => WornLayer | null;
}

/** Position a box (fractions of the canvas) in percent. */
const boxStyle = ([x0, y0, x1, y1]: AvatarBox): CSSProperties => ({
  left: `${x0 * 100}%`,
  top: `${y0 * 100}%`,
  width: `${(x1 - x0) * 100}%`,
  height: `${(y1 - y0) * 100}%`,
});

/** Grow a box about its centre to at least `MIN_TARGET` on each side. */
function target([x0, y0, x1, y1]: AvatarBox): AvatarBox {
  const w = Math.max(x1 - x0, MIN_TARGET);
  const h = Math.max(y1 - y0, MIN_TARGET);
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  return [cx - w / 2, cy - h / 2, cx + w / 2, cy + h / 2];
}

/**
 * The avatar: the mannequin body with each equipped item's worn layer stacked on it, back to front.
 * Every layer was drawn on this mannequin, so it is placed exactly where its box says, with no fitting.
 * Placed items carry `data-testid="avatar-slot-<slot>"`; an item without a worn layer is not drawn.
 */
export function AvatarView({ avatar, size = 280, onSlotTap, resolveWorn = resolveWornLayer }: Props) {
  const body = avatarBodyFor(avatar.body);
  const layers: ReactNode[] = [];
  for (const slot of STACK_ORDER) {
    const itemId = avatar.equipped[slot];
    const layer = itemId ? resolveWorn(itemId) : null;
    if (!layer) continue;
    layers.push(<img key={slot} className="worn" data-testid={`avatar-slot-${slot}`} src={layer.url} alt="" decoding="async" style={boxStyle(layer.box)} />);
  }
  return (
    <div className="avatar" style={{ width: `min(${size}px, 100%)`, aspectRatio: '1' }} data-testid="avatar-view">
      <img className="avatar-body" src={body.image} alt="" decoding="async" data-testid="avatar-body" />
      {layers}
      {onSlotTap &&
        STACK_ORDER.map((slot) => (
          <button
            key={slot}
            type="button"
            className="hit"
            data-testid={`avatar-hit-${slot}`}
            aria-label={slotLabel(slot)}
            style={boxStyle(target(body.zones[slot]))}
            onClick={() => onSlotTap(slot)}
          />
        ))}
    </div>
  );
}
